import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import type { Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import type { InterviewResult } from '@core/interview-results-types';
import type { EvaluationPanelData, EvaluationPanelOffer } from '@recruitee/evaluations-types';

import { EvaluationPanelActionsService } from './evaluation-panel-actions.service';
import { QuickEvaluationFormService } from './quick-evaluation-form.service';
import { TemplateEvaluationFormService } from './template-evaluation-form.service';

@Injectable()
export class EvaluationPanelContentLoaderService {
  private readonly actions = inject(EvaluationPanelActionsService);
  private readonly quickForm = inject(QuickEvaluationFormService);
  private readonly templateForm = inject(TemplateEvaluationFormService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly templateName = signal('');
  public readonly templateInfo = signal<{ title: string; description: string }>({
    title: '',
    description: '',
  });
  public readonly templateApplied = signal(false);
  public readonly editedResult = signal<InterviewResult | null>(null);

  private loadedTemplateKey: string | undefined;
  private loadedEditResultId: number | undefined;

  public reset(): void {
    this.loadedTemplateKey = undefined;
    this.loadedEditResultId = undefined;
    this.templateApplied.set(false);
    this.editedResult.set(null);
  }

  public loadTemplate(data: EvaluationPanelData, offer: EvaluationPanelOffer | undefined): void {
    if (data.quickEvaluation || !data.templateId) return;
    if (data.edit && data.resultId) return;
    if (data.offerId && !offer) return;

    const jobPositionId =
      data.hasPositionsFeature && offer && 'jobPositionId' in offer ? offer.jobPositionId : null;
    const templateKey = `${data.templateId}:${jobPositionId ?? ''}`;
    if (this.loadedTemplateKey === templateKey) return;
    this.loadedTemplateKey = templateKey;

    this.actions
      .fetchTemplate(data.templateId, jobPositionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(template => {
        this.templateName.set(template.name);
        this.templateInfo.set({
          title: template.infoTitle,
          description: template.infoDescription,
        });
        this.templateForm.applyTemplate(template);
        this.templateApplied.set(true);
      });
  }

  public loadEditedTemplate(data: Signal<EvaluationPanelData>): void {
    const current = data();
    if (current.quickEvaluation || !current.edit || !current.resultId) return;
    if (this.loadedEditResultId === current.resultId) return;
    this.loadedEditResultId = current.resultId;

    this.actions
      .fetchInterviewResult(current.resultId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (data().resultId !== result.id) return;

        this.editedResult.set(result);
        this.templateName.set(result.interviewTemplateName);
        this.templateInfo.set({
          title: result.interviewTemplateInfoTitle,
          description: result.interviewTemplateInfoDescription,
        });
        this.templateForm.applyResult(result);
        this.templateApplied.set(true);
      });
  }

  public loadEditedQuick(data: Signal<EvaluationPanelData>, onLoaded: () => void): void {
    const current = data();
    const offerControl = this.quickForm.form.controls.offerId;

    if (!current.quickEvaluation || !current.edit || !current.resultId) {
      if (offerControl.disabled) {
        offerControl.enable();
      }
      return;
    }

    offerControl.disable();

    this.actions
      .fetchInterviewResult(current.resultId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (data().resultId !== result.id) return;

        this.editedResult.set(result);
        this.quickForm.patchFromResult(result);
        onLoaded();
      });
  }
}
